import { cva, type VariantProps } from 'class-variance-authority'
import { ButtonHTMLAttributes, DetailedHTMLProps, forwardRef } from 'react'
import { ImSpinner } from 'react-icons/im'

import { cn } from '../libs/tailwindMerge'

const buttonVariants = cva(
  'flex w-full justify-center items-center text-sm/6 font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 transition',
  {
    variants: {
      variant: {
        default: 'bg-indigo-600 text-white hover:bg-indigo-500 shadow-sm focus-visible:outline-indigo-600',
        danger: 'bg-rose-600 text-white hover:bg-rose-500 shadow-sm focus-visible:outline-indigo-600',
        ghost: 'bg-transparent text-gray-800 hover:text-indigo-900 hover:bg-indigo-100 focus-visible:outline-indigo-600',
        outline: 'bg-transparent border border-indigo-600 text-indigo-600 shadow-sm hover:bg-indigo-600 hover:text-white focus-visible:outline-indigo-600',
      },
      size: {
        default: 'h-8 sm:h-10 rounded-md px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        xs: 'h-5 rounded-md px-1 py-2',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-8 sm:h-10 rounded-md w-8 sm:w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

interface ButtonProps
  extends DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  isLoading?: boolean
  hasPermission?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant, size, className, children, isLoading = false, hasPermission = true, ...rest },
  ref,
) {
  if (!hasPermission) return null
  return (
    <button
      ref={ref}
      disabled={isLoading}
      className={cn(
        buttonVariants({ variant, size, className }),
      )}
      {...rest}
    >
      {!isLoading
        ? children
        : <ImSpinner className="animate-spin" />}
    </button>
  )
})
